import MainLayout from 'layout/MainLayout';
import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import Loadable from 'ui-component/Loadable';
import AdminProtectedRoute from './AdminProtectedRoutes';
import ProtectedRoute from './ProtectedRoutes';

const Suppliers = Loadable(lazy(() => import('views/pages/reports/database/Suppliers')));
const Contractors = Loadable(lazy(() => import('views/pages/reports/database/Contractors')));
const CompletedProject = Loadable(lazy(() => import('views/pages/reports/project-report/CompletedProject')));
const WaitingProject = Loadable(lazy(() => import('views/pages/reports/project-report/WaitingProject')));
const DLP = Loadable(lazy(() => import('views/pages/reports/project-report/DLP')));
const DeletedProjects = Loadable(lazy(() => import('views/pages/reports/project-report/DeletedProjects')));
const CancelledProjects = Loadable(lazy(() => import('views/pages/reports/project-report/CancelledProjects')));
const DeferredProjects = Loadable(lazy(() => import('views/pages/reports/project-report/DeferredProjects')));
const OngoingProjects = Loadable(lazy(() => import('views/pages/reports/project-report/OngoingProjects')));


const Project = Loadable(lazy(() => import('ui-component/project')));
const ProjectNumber = Loadable(lazy(() => import('ui-component/project_number')));
const NewContract = Loadable(lazy(() => import('ui-component/contract')));


const ContractorDetails = Loadable(lazy(() => import('ui-component/supplier_contractor/ContractorDetails')));
const SupplierDetails = Loadable(lazy(() => import('ui-component/supplier_contractor/SupplierDetails')));

const Users = Loadable(lazy(() => import('views/pages/dashbord/utilities/Users')));
const RolesPage = Loadable(lazy(() => import('views/pages/dashbord/utilities/RolesPage')));
const DepartmentsPage = Loadable(lazy(() => import('views/pages/dashbord/utilities/DepartmentsPage')));
const Funds = Loadable(lazy(() => import('views/pages/dashbord/utilities/Funds')));
const AdminReportsPage = Loadable(lazy(() => import('views/pages/dashbord/utilities/AdminReportsPage')));
const FundDetailPage = Loadable(lazy(() => import('views/pages/dashbord/utilities/FundDetails')));
const SectorDetailPage = Loadable(lazy(() => import('views/pages/dashbord/utilities/SectorDetailPage')));
const FinancialYearsPage = Loadable(lazy(() => import('views/pages/dashbord/utilities/FinancialYear')));
const Projects = Loadable(lazy(() => import('views/pages/dashbord/utilities/Projects')));
const ProjectNumbers = Loadable(lazy(() => import('views/pages/dashbord/utilities/ProjectNumber')));
const Sectors = Loadable(lazy(() => import('views/pages/dashbord/utilities/Sectors')));
const ManageSectors = Loadable(lazy(() => import('ui-component/utils/ManageSectors')));
const ManageFunds = Loadable(lazy(() => import('ui-component/utils/ManageFunds')));
const AccountSettings = Loadable(lazy(() => import('ui-component/account-settings')));
const Reports = Loadable(lazy(() => import('views/pages/dashbord/utilities/Reports')));
const Contracts = Loadable(lazy(() => import('views/pages/dashbord/utilities/Contracts')));
const Gallery = Loadable(lazy(() => import('views/pages/dashbord/utilities/Gallery')));

// dashboard routing
const DashboardDefault = Loadable(lazy(() => import('views/dashboard/Default')));
const DashboardCDSF = Loadable(lazy(() => import('views/dashboard/Default/indexCDSF')));
const DashboardMOET = Loadable(lazy(() => import('views/dashboard/Default/indexMOET')));
const DashboardCF = Loadable(lazy(() => import('views/dashboard/Default/indexCF')));
const DashboardRDF = Loadable(lazy(() => import('views/dashboard/Default/indexRDF')));

// sample page routing
const NotAuthorized = Loadable(lazy(() => import('views/pages/NotAuthorized')));
const UnderConstruction = Loadable(lazy(() => import('views/pages/under-construction')));
const AuditLogsPage = Loadable(lazy(() => import('views/pages/dashbord/utilities/AuditLog')));
const SectorProjects = Loadable(lazy(() => import('views/pages/dashbord/utilities/SectorProjects')));
const FundDashboard = Loadable(lazy(() => import('views/dashboard/Default/FundDashboard')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
    path: '/',
    element: (
        <AdminProtectedRoute>
            <MainLayout />
        </AdminProtectedRoute>
    ),
    children: [
        {
            path: '/',
            element: <Navigate to="/dashboard/default" replace />
        },
        {
            path: '/dashboard/default',
            element: <DashboardDefault />
        },
        {
            path: '/sectors/:sectorId/:slug',
            element: <SectorProjects />
        },
        {
            path: '/dashboard/funds/:fundId/:slug',
            element: <FundDashboard />
        },
        {
            path: '/dashboard/cdsf',
            element: <DashboardCDSF />
        },
        {
            path: '/dashboard/moet',
            element: <DashboardMOET />
        },
        {
            path: '/dashboard/cf',
            element: <DashboardCF />
        },
        {
            path: '/dashboard/rdf',
            element: <DashboardRDF />
        },

        {
            path: '/under-construction',
            element: <UnderConstruction />
        },
        {
            path: '/completed-projects',
            element: <CompletedProject />
        },
        {
            path: '/dlp-projects',
            element: <DLP />
        },
        {
            path: '/cancelled-projects',
            element: <CancelledProjects />
        },
        {
            path: '/deferred-projects',
            element: <DeferredProjects />
        },
        {
            path: '/ongoing-projects',
            element: <OngoingProjects />
        },
        {
            path: '/to-be-launched-projects',
            element: <WaitingProject />
        },
        {
            path: '/deleted-projects',
            element: <DeletedProjects />
        },
        {
            path: '/contractors',
            element: <Contractors />
        },
        {
            path: '/contractor/:id',
            element: (
                <ProtectedRoute permissions={['contractors.read', 'contractors.update']}>
                    <ContractorDetails />
                </ProtectedRoute>
            )
        },
        {
            path: '/new_contractor',
            element: (
                <ProtectedRoute permissions={['contractors.create']}>
                    <ContractorDetails />
                </ProtectedRoute>
            )
        },
        {
            path: '/suppliers',
            element: <Suppliers />
        },
        {
            path: '/new_supplier',
            element: (
                <ProtectedRoute permissions={['suppliers.create']}>
                    <SupplierDetails />
                </ProtectedRoute>
            )
        },
        {
            path: '/supplier/:id',
            element: (
                <ProtectedRoute permissions={['suppliers.read', 'suppliers.update']}>
                    <SupplierDetails />
                </ProtectedRoute>
            )
        },
        {
            path: '/gallery',
            element: <Gallery />
        },
        {
            path: '/financial_year',
            element: <FinancialYearsPage />
        },
        {
            path: '/reports-analytics',
            element: <AdminReportsPage />
        },
        {
            path: '/contracts',
            element: <Contracts />
        },
        {
            path: 'admin/project',
            element: <Project />
        },
        {
            path: 'admin/project_number',
            element: <ProjectNumber />
        },
        {
            path: 'admin/new_contract',
            element: <NewContract />
        },
        {
            path: 'admin/edit_contract/:id',
            element: <NewContract />
        },
        {
            path: 'admin/project/:id',
            element: <Project />
        },
        {
            path: 'admin/project_number/:id',
            element: <ProjectNumber />
        },
        {
            path: '/funds',
            element: <Funds />
        },
        {
            path: '/fund/:id/edit',
            element: <ManageFunds />
        },
        {
            path: '/fund/:id',
            element: <FundDetailPage />
        },
        {
            path: '/sectors',
            element: <Sectors />
        },
        {
            path: '/sector/:id/edit',
            element: <ManageSectors />
        },
        {
            path: '/sector/:id',
            element: <SectorDetailPage />
        },
        {
            path: '/new_sector',
            element: <ManageSectors />
        },
        {
            path: '/new_fund',
            element: <ManageFunds />
        },
        {
            path: '/projects',
            element: (
                <ProtectedRoute permissions={['projects.read']}>
                    <Projects />
                </ProtectedRoute>
            )
        },
        {
            path: '/project_number',
            element: (
                <ProtectedRoute permissions={['projects.create']}>
                    <ProjectNumbers />
                </ProtectedRoute>
            )
        },
        {
            path: '/users',
            element: (
                <ProtectedRoute roles={['super_admin', 'system_admin']} permissions={['users.read']}>
                    <Users />
                </ProtectedRoute>
            )
        },
        {
            path: '/roles',
            element: (
                <ProtectedRoute roles={['super_admin', 'system_admin']} permissions={['users.manage_roles']}>
                    <RolesPage />
                </ProtectedRoute>
            )
        },
        {
            path: '/departments',
            element: (
                <ProtectedRoute roles={['super_admin', 'system_admin']} permissions={['departments.read']}>
                    <DepartmentsPage />
                </ProtectedRoute>
            )
        },
        {
            path: '/unauthorized',
            element: <NotAuthorized />
        },
        {
            path: '/account_settings',
            element: <AccountSettings />
        },
        {
            path: '/reports',
            element: (
                <ProtectedRoute permissions={['reports.read']}>
                    <Reports />
                </ProtectedRoute>
            )
        },
        {
            path: '/audit-logs',
            element: (
                <ProtectedRoute roles={['super_admin', 'system_admin', 'department_head']} permissions={['audit_logs.read']}>
                    <AuditLogsPage />
                </ProtectedRoute>
            )
        }
    ]
};

export default MainRoutes;
